/**
 * CartPanel.js — Shopping Cart Panel (message kind "cart").
 *
 * Parity with old createCartProducts (script.js 3218-3290): each line shows
 * image, title, "{price} per bottle", price, and a trash + qty-select + Update
 * control; below them a Subtotal / Estimated total summary and a checkout link.
 *
 * INTERACTIVE + LIVE:
 *   Line controls emit ws-cart-remove / ws-cart-update; features/optional/cart.js
 *   owns the BE calls and writes the new cart (items + summary) into state. The
 *   panel re-renders from state.cart on ws-cart-count, so edits reflect in place.
 *
 * LINE FIELDS (apps/integrations/shopify/utils.py):
 *   image, productTitle, price, productVariantId, quantity
 * SUMMARY (state.cart.summary / data.cart.cart): subTotal, bottleDepositTotal,
 *   total, checkoutUrl
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js createCartProducts() (3218-3290)
 *   RedConcierge/static/javascript/main.js createCartProducts — Bottle Deposit Total
 */

import { emit, on, getState } from "../../core/state.js";
import { getFeature } from "../../core/configAccessors.js";

const MAX_QTY = 50; // old productMaxQty (script.js)

const TRASH_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.00004H14M12.6667 4.00004V13.3334C12.6667 14 12 14.6667 11.3333 14.6667H4.66667C4 14.6667 3.33333 14 3.33333 13.3334V4.00004M5.33333 4.00004V2.66671C5.33333 2.00004 6 1.33337 6.66667 1.33337H9.33333C10 1.33337 10.6667 2.00004 10.6667 2.66671V4.00004" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export default class CartPanel {
  get showBottleDepositTotal() {
    return true;
  }

  render(message) {
    const panel = document.createElement("div");
    panel.className = "ws-cart-panel";

    // Content holder is rebuilt on every render; the overlay is a sibling so it
    // survives the rebuild and stays on top while a mutation is in flight.
    const content = document.createElement("div");
    content.className = "ws-cart-content";
    panel.appendChild(content);

    // Spinner overlay shown during cart mutations — parity with the old
    // .overlay-effect that covered the cart while the BE call ran.
    const overlay = document.createElement("div");
    overlay.className = "ws-cart-overlay";
    overlay.hidden = true;
    panel.appendChild(overlay);

    // Flipped true once this panel sees a mutation (ws-cart-count). After that,
    // state.cart is authoritative — including an empty cart (last line removed).
    // Before it, prefer whichever source actually has items so the panel never
    // renders blank when state lags the message snapshot it was created with.
    let mutated = false;

    const renderInto = () => {
      const cart = getState().cart || {};
      const stateItems = Array.isArray(cart.items) ? cart.items : [];
      const msgItems = Array.isArray(message?.data?.items) ? message.data.items : [];
      const items = mutated ? stateItems : stateItems.length ? stateItems : msgItems;
      const summary =
        cart.summary || message?.data?.cart?.cart || message?.data?.cart || {};

      content.replaceChildren();

      if (!items.length) {
        const empty = document.createElement("p");
        empty.className = "ws-cart-empty";
        empty.textContent = cart.emptyMessage;
        content.appendChild(empty);
        return;
      }

      items.forEach((item) => content.appendChild(this._row(item)));

      const sum = document.createElement("div");
      sum.className = "ws-cart-summary";
      if (summary.subTotal) sum.appendChild(this._summaryRow("Subtotal", summary.subTotal, false));
      // Old createCartProducts: Bottle Deposit Total between Subtotal and Estimated total.
      if (this.showBottleDepositTotal && summary.bottleDepositTotal) {
        sum.appendChild(
          this._summaryRow("Bottle Deposit Total", summary.bottleDepositTotal, false),
        );
      }
      if (summary.total) sum.appendChild(this._summaryRow("Estimated total", summary.total, true));
      content.appendChild(sum);

      content.appendChild(this._renderActions());
    };

    renderInto();
    on("ws-cart-count", () => {
      mutated = true;
      if (panel.isConnected) renderInto();
    });
    // Toggle the spinner overlay while cart.js runs a mutation (add/update/remove).
    on("ws-cart-busy", ({ active } = {}) => {
      if (panel.isConnected) overlay.hidden = !active;
    });
    return panel;
  }

  // Shared extension point: variants can replace actions without duplicating
  // cart rows, live updates, or the mutation overlay.
  _renderActions() {
    // Default footer: checkout + continue shopping.
    // Proceed emits ws-checkout, which starts the Ship/Pickup flow (Part B).
    const footer = document.createElement("div");
    footer.className = "ws-cart-actions";

    const checkout = document.createElement("button");
    checkout.type = "button";
    checkout.className = "ws-cart-action-btn";
    checkout.textContent = "Proceed to checkout";
    checkout.addEventListener("click", () => {
      // Mark the clicked action selected (same pattern as a clicked prompt
      // chip, PromptList.js) — unlike CheckoutOptions' Ship/Pickup, the
      // other button stays enabled since the cart itself is still usable.
      checkout.classList.add("is-selected");
      emit("ws-checkout");
    });

    // Continue Shopping → sendMessage(
    // 'Show me all available wines', true) so the Shop Our Wines cards reload.
    const continueShopping = document.createElement("button");
    continueShopping.type = "button";
    continueShopping.className = "ws-cart-action-btn";
    continueShopping.textContent = "Continue Shopping";
    continueShopping.addEventListener("click", () => {
      continueShopping.classList.add("is-selected");
      emit("ws-prompt-select", {
        label: "Show me all available wines",
        value: "Show me all available wines",
      });
    });

    footer.appendChild(checkout);
    footer.appendChild(continueShopping);
    return footer;
  }

  _row(item) {
    // Commerce7 cart lines are keyed by sku (cart_c7.js listens for {sku}),
    // not productVariantId — same underlying identifier, different field name
    // on both the line item and the outgoing cart events.
    const isC7 = getFeature("commerce7");
    const idKey = isC7 ? "sku" : "variantId";
    const variantId = isC7 ? item.sku : item.productVariantId;
    const row = document.createElement("div");
    row.className = "ws-cart-item";

    if (item.image) {
      const img = document.createElement("img");
      img.className = "ws-cart-item-img";
      img.src = item.image;
      img.alt = item.productTitle || "Item";
      img.loading = "lazy";
      row.appendChild(img);
    }

    const body = document.createElement("div");
    body.className = "ws-cart-item-body";

    const productDetails = document.createElement("div");
    productDetails.className="ws-cart-product-details";

    const title = document.createElement("p");
    title.className = "ws-cart-item-title";
    title.textContent = item.productTitle || "";
    body.appendChild(title);

    if (item.price) {
      const desc = document.createElement("p");
      desc.className = "ws-cart-item-meta";
      desc.textContent = `${item.price} per bottle`;
      body.appendChild(desc);

      // Bold per-item price (old createCartProducts shows it under the meta).
      const price = document.createElement("p");
      price.className = "ws-cart-item-price";
      price.textContent = item.price;
      productDetails.appendChild(price);
    }

    // Line control: trash + qty select + Update (reuses the wine-card styles).
    const action = document.createElement("div");
    action.className = "ws-cp-action ws-cart-item-action";

    const del = document.createElement("button");
    del.type = "button";
    del.className = "ws-cp-delete";
    del.setAttribute("aria-label", "delete from cart");
    del.title = "delete from cart";
    del.innerHTML = TRASH_ICON;
    // fromCart: this edit came from within the cart panel — it re-renders in
    // place (ws-cart-count), so cart.js skips appending a duplicate summary.
    del.addEventListener("click", () => emit("ws-cart-remove", { [idKey]: variantId, fromCart: true }));

    const qtyWrap = document.createElement("div");
    qtyWrap.className = "ws-cp-qty-wrap cart-quantity-wrapper";

    const dec = document.createElement("button");
    dec.type = "button";
    dec.className = "ws-cp-dec dec-qty";
    dec.setAttribute("aria-label", "Decrease quantity");
    dec.innerHTML = '<span aria-hidden="true">−</span>';
    // fromCart on +/− too (like the trash button): the panel re-renders in place,
    // so cart.js must not post another order summary. Old .cart-container
    // .dec-qty: at qty 1, minus removes the line (handleDeleteFromViewCart).
    dec.addEventListener("click", () => {
      if (item.quantity <= 1) {
        emit("ws-cart-remove", { [idKey]: variantId, fromCart: true });
        return;
      }
      emit("ws-cart-update", { [idKey]: variantId, quantity: item.quantity - 1, fromCart: true });
    });

    const qty = document.createElement("div");
    qty.className = "ws-we-cart-qty cart-qty";
    qty.textContent = String(item.quantity);

    const inc = document.createElement("button");
    inc.type = "button";
    inc.className = "ws-cp-inc inc-qty";
    inc.setAttribute("aria-label", "Increase quantity");
    inc.innerHTML = '<span aria-hidden="true">+</span>';
    inc.addEventListener("click", () =>
      emit("ws-cart-update", {
        [idKey]: variantId,
        quantity: Math.min(12, item.quantity + 1),
        fromCart: true,
      }),
    );

    action.appendChild(qtyWrap);
    qtyWrap.append(del,dec,qty,inc)
    // action.appendChild(select);
    // action.appendChild(update);
    productDetails.appendChild(action);
    body.appendChild(productDetails);
    row.appendChild(body);
    return row;
  }

  _summaryRow(label, value, isTotal) {
    const row = document.createElement("div");
    row.className = isTotal ? "ws-cart-summary-row ws-cart-total" : "ws-cart-summary-row";
    const l = document.createElement("span");
    l.textContent = label;
    const v = document.createElement("span");
    v.textContent = value;
    row.appendChild(l);
    row.appendChild(v);
    return row;
  }
}
